import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createSlots as _createSlots, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-0b84e453"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  key: 0,
  class: "asset-management-plan"
}
const _hoisted_2 = { class: "draw-actions" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("small", { class: "d-block mt-2" }, " This will create a copy based on the current plan and set it as the latest revision. ", -1))
const _hoisted_4 = {
  key: 1,
  class: "loading-page"
}

import {
  AssetManagementPlan,
  FLOCBoundary,
  IndividualAssetManagementPlan,
  RACIData,
  RACIRole,
  RACITask,
  ReliabilityMetricsLink,
  RiskRegisterLink,
  WorkManagementMetricsLink,
} from "@/classes";
import GeneralComponent from "@/components/asset-management-plan/General.vue";
import IntroductionComponent from "@/components/asset-management-plan/sections/Introduction.vue";
import RACIComponent from "@/components/asset-management-plan/sections/RACI.vue";
import ScopeBoundaryLimitsComponent from "@/components/asset-management-plan/sections/ScopeBoundaryLimits.vue";
import AssetInformationComponent from "@/components/asset-management-plan/sections/AssetInformation.vue";
import AssetManagementPlanComponent from "@/components/asset-management-plan/sections/AssetManagementPlan.vue";
import ServiceLevelsKPIsComponent from "@/components/asset-management-plan/sections/ServiceLevelsKPIs.vue";
import LifeCycleCostModellingComponent from "@/components/asset-management-plan/sections/LifeCycleCostModelling.vue";
import ImprovementActionsComponent from "@/components/asset-management-plan/sections/ImprovementActions.vue";
import { setupApiQueue } from "@/components/base/api-queue";
import { ref, onMounted, computed, watch } from "vue";
import { onBeforeRouteLeave, useRoute, useRouter } from "vue-router";
import { useNotifierStore } from "@/store/notifier";
import { useConfirmStore } from "@/store/confirm";
import type {
  Deleteable,
  DeleteableKey,
} from "@/classes/asset-management-plan";
import TooltipIcon from "@/components/base/tooltips/TooltipIcon.vue";
import BaseDialog from "@/components/base/dialogs/BaseDialog.vue";
import { useAMPStore } from "@/store/asset-management-plan";
import { useFilterStore } from "@/store/filter";
import { Section, sections } from "@/components/asset-management-plan/sections";


export default /*#__PURE__*/_defineComponent({
  __name: 'asset-management-plan',
  setup(__props) {

const { queueApiAction, loading } = setupApiQueue();

const $route = useRoute();
const $router = useRouter();
const $notifier = useNotifierStore();

const store = useAMPStore();
const filterStore = useFilterStore();
filterStore.hide();

const readonly = computed(() => {
  if (store.plan)
    return (
      store.plan.status !== "Draft" ||
      store.plan.id !==
        store.plan.asset_management_plan_master.metrics.latest_revision_id
    );
  return true;
});

const refreshPlan = () => {
  queueApiAction(async () => {
    store.plan = null;
    const queryParam = $route.query.id;
    if (Array.isArray(queryParam) || !queryParam) {
      $notifier.error("Could not find revision.");
      $router.push("/asset-management-plans");
    } else {
      store.plan = await AssetManagementPlan.get(String($route.query.id));
      store.plan.fixRelationships();
      await store.getPreventativeMaintenance();
    }
  });
};

const publishing = ref(false);
const publish = () => {
  $confirm.open({
    title: "Do you want to publish this revision?",
    content:
      "This will save the current revision and set the status to In Review. During the approval process, this revision cannot be edited.",
    color: "primary",
    onConfirm: async () => {
      publishing.value = true;
      store.plan.status = "In Review";
      await save();
      publishing.value = false;
      await refreshPlan();
    },
  });
};

const archiving = ref(false);
const archive = () => {
  $confirm.open({
    title: "Do you want to archive this revision?",
    content:
      "This will set the current revision to read-only mode and stop any ongoing approvals.",
    color: "primary",
    onConfirm: async () => {
      archiving.value = true;
      store.plan.status = "Archived";
      await save();
      archiving.value = false;
      await refreshPlan();
    },
  });
};

const deleteIds = ref<Record<DeleteableKey, string[]>>({
  individual_asset_management_plan: [],
  raci_task: [],
  raci_role: [],
  raci_data: [],
  floc_boundary: [],
  risk_register_links: [],
  work_management_metrics_links: [],
  reliability_metrics_links: [],
});

const deleteItem = (item: Deleteable) => {
  if (item instanceof IndividualAssetManagementPlan) {
    deleteIds.value.individual_asset_management_plan.push(item.id);
  }
  if (item instanceof RACIRole) {
    deleteIds.value.raci_role.push(item.id);
  }
  if (item instanceof RACITask) {
    deleteIds.value.raci_task.push(item.id);
  }
  if (item instanceof RACIData) {
    deleteIds.value.raci_data.push(item.id);
  }
  if (item instanceof FLOCBoundary) {
    deleteIds.value.floc_boundary.push(item.id);
  }
  if (item instanceof RiskRegisterLink) {
    deleteIds.value.risk_register_links.push(item.id);
  }
  if (item instanceof WorkManagementMetricsLink) {
    deleteIds.value.work_management_metrics_links.push(item.id);
  }
  if (item instanceof ReliabilityMetricsLink) {
    deleteIds.value.reliability_metrics_links.push(item.id);
  }
};

const save = async () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
  await queueApiAction(async () => {
    await store.plan.update(deleteIds.value);
    for (const key in deleteIds.value) {
      deleteIds.value[key] = [];
    }
  });
};

const createRevisionDialog = ref(false);
const newRevisionName = ref("");
const openCreateRevisionDialog = () => {
  createRevisionDialog.value = true;
  newRevisionName.value = "";
};

const creatingRevision = ref(false);
const createRevision = async () => {
  creatingRevision.value = true;
  const copyId = await AssetManagementPlan.$axios.$post<string>(
    "/asset-management-plan/copy",
    {
      id: store.plan.id,
      revision_name: newRevisionName.value,
    },
  );
  creatingRevision.value = false;
  createRevisionDialog.value = false;
  tabKey.value = "general";
  $router.push(`/asset-management-plan?id=${copyId}`);
};

const $confirm = useConfirmStore();
onBeforeRouteLeave(async () => {
  if (readonly.value) return;
  await new Promise<void>((resolve, reject) => {
    try {
      $confirm.open({
        title: "Do you want to save your work?",
        content: `Save "${store.plan.asset_management_plan_master.name}-${store.plan.revision}" before exiting.`,
        confirmText: ["Save", "Don't Save"],
        confirmArgs: [[true], [false]],
        cancelText: "Cancel",
        color: "primary",
        onConfirm: (shouldSave) => {
          if (shouldSave) {
            save().then(() => {
              resolve();
            });
          } else {
            resolve();
          }
        },
        onCancel: () => {
          reject();
        },
      });
    } catch (err) {
      console.error(err);
      $notifier.error(err);
      reject(err);
    }
  });
});

const validSections = computed<Record<string, boolean>>(() => {
  if (store.plan) {
    return {
      introduction: store.plan.valid_section_introduction,
      raci: store.plan.valid_section_raci,
      "scope-boundary-limits": store.plan.valid_section_scope_boundary_limits,
      "asset-information": store.plan.valid_section_asset_information,
      "asset-management-plan": store.plan.valid_section_asset_management_plan,
      "service-levels-kpis": store.plan.valid_section_service_levels_kpis,
      "life-cycle-cost-modelling":
        store.plan.valid_section_life_cycle_cost_modelling,
      "improvement-actions": store.plan.valid_section_improvement_actions,
    };
  }
  return {};
});

const tab = ref<Section>(sections[0]);

const tabKey = computed({
  get: () => {
    return tab.value.key;
  },
  set: (val: string) => {
    const section = sections.find((h) => h.key === val) ?? tab.value;
    tab.value = section;
    $router.replace({
      path: "/asset-management-plan",
      query: { ...$route.query, section: section.key },
    });
    $route.query.section = tab.value.key;
  },
});

watch(
  () => $route.query.id,
  () => {
    tabKey.value = String($route.query.section);
    refreshPlan();
  },
  { immediate: true },
);

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_navigation_drawer = _resolveComponent("v-navigation-drawer")!
  const _component_v_tabs_window_item = _resolveComponent("v-tabs-window-item")!
  const _component_v_tabs_window = _resolveComponent("v-tabs-window")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!

  return (!!_unref(store).plan)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_component_v_navigation_drawer, null, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_component_v_btn, {
                icon: "mdi-arrow-left",
                density: "compact",
                variant: "plain",
                disabled: !!_unref(loading),
                onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref($router).push('/asset-management-plans')))
              }, null, 8, ["disabled"]),
              _createVNode(_component_v_spacer),
              (!readonly.value)
                ? (_openBlock(), _createBlock(_component_v_btn, {
                    key: 0,
                    color: "secondary",
                    flat: "",
                    loading: !!_unref(loading),
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (save()))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, { class: "mr-1" }, {
                        default: _withCtx(() => [
                          _createTextVNode("mdi-content-save")
                        ]),
                        _: 1
                      }),
                      _createTextVNode("Save ")
                    ]),
                    _: 1
                  }, 8, ["loading"]))
                : (
            _unref(store).plan.id ===
              _unref(store).plan.asset_management_plan_master.metrics
                .latest_revision_id &&
            (_unref(store).plan.status === 'Published' ||
              _unref(store).plan.status === 'Archived')
          )
                  ? (_openBlock(), _createBlock(_component_v_btn, {
                      key: 1,
                      color: "secondary",
                      flat: "",
                      loading: !!_unref(loading),
                      onClick: openCreateRevisionDialog
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, { class: "mr-1" }, {
                          default: _withCtx(() => [
                            _createTextVNode("mdi-plus")
                          ]),
                          _: 1
                        }),
                        _createTextVNode("Revision ")
                      ]),
                      _: 1
                    }, 8, ["loading"]))
                  : _createCommentVNode("", true)
            ]),
            _createVNode(_component_v_divider),
            _createVNode(_component_v_list_item, {
              title: "Asset Management Plan",
              subtitle: 
          (_unref(store).plan.asset_management_plan_master.name ?? '') +
          '-' +
          (_unref(store).plan.revision ?? '')
        
            }, null, 8, ["subtitle"]),
            _createVNode(_component_v_divider),
            _createVNode(_component_v_list, {
              modelValue: tabKey.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((tabKey).value = $event))
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(sections), (section) => {
                  return (_openBlock(), _createBlock(_component_v_list_item, {
                    key: section.key,
                    link: "",
                    title: section.title,
                    active: section.key === tabKey.value,
                    onClick: ($event: any) => (tabKey.value = section.key)
                  }, _createSlots({ _: 2 }, [
                    (validSections.value[section.key] === false)
                      ? {
                          name: "append",
                          fn: _withCtx(() => [
                            _createVNode(TooltipIcon, { text: "Please fill out all fields." })
                          ]),
                          key: "0"
                        }
                      : undefined
                  ]), 1032, ["title", "active", "onClick"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        }),
        _createVNode(_component_v_tabs_window, {
          "model-value": tabKey.value,
          direction: "vertical",
          class: "content"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_tabs_window_item, {
              value: "general",
              key: "general"
            }, {
              default: _withCtx(() => [
                _createVNode(GeneralComponent, {
                  class: "tab-window",
                  readonly: readonly.value,
                  onDelete: deleteItem,
                  onRefresh: refreshPlan
                }, {
                  "revision-actions": _withCtx(() => [
                    (!readonly.value || publishing.value)
                      ? (_openBlock(), _createBlock(_component_v_btn, {
                          key: 0,
                          class: "ml-2",
                          color: "secondary",
                          flat: "",
                          disabled: !_unref(store).plan.valid || !!_unref(loading),
                          loading: publishing.value,
                          onClick: publish
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, {
                              class: "mr-1",
                              icon: "mdi-send"
                            }),
                            _createTextVNode(" Publish ")
                          ]),
                          _: 1
                        }, 8, ["disabled", "loading"]))
                      : _createCommentVNode("", true),
                    (
                _unref(store).plan.status === 'Draft' ||
                _unref(store).plan.status === 'In Review'
              )
                      ? (_openBlock(), _createBlock(_component_v_btn, {
                          key: 1,
                          class: "ml-2",
                          variant: "outlined",
                          disabled: !!_unref(loading),
                          loading: archiving.value,
                          onClick: archive
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, {
                              class: "mr-1",
                              icon: "mdi-archive"
                            }),
                            _createTextVNode(" Archive ")
                          ]),
                          _: 1
                        }, 8, ["disabled", "loading"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["readonly"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_tabs_window_item, {
              value: "introduction",
              key: "introduction"
            }, {
              default: _withCtx(() => [
                _createVNode(IntroductionComponent, {
                  class: "tab-window",
                  readonly: readonly.value,
                  onDelete: deleteItem
                }, null, 8, ["readonly"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_tabs_window_item, {
              value: "raci",
              key: "raci"
            }, {
              default: _withCtx(() => [
                _createVNode(RACIComponent, {
                  class: "tab-window",
                  readonly: readonly.value,
                  onDelete: deleteItem
                }, null, 8, ["readonly"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_tabs_window_item, {
              value: "scope-boundary-limits",
              key: "scope-boundary-limits"
            }, {
              default: _withCtx(() => [
                _createVNode(ScopeBoundaryLimitsComponent, {
                  class: "tab-window",
                  readonly: readonly.value,
                  onDelete: deleteItem
                }, null, 8, ["readonly"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_tabs_window_item, {
              value: "asset-information",
              key: "asset-information"
            }, {
              default: _withCtx(() => [
                _createVNode(AssetInformationComponent, {
                  class: "tab-window",
                  readonly: readonly.value,
                  onDelete: deleteItem
                }, null, 8, ["readonly"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_tabs_window_item, {
              value: "asset-management-plan",
              key: "asset-management-plan"
            }, {
              default: _withCtx(() => [
                _createVNode(AssetManagementPlanComponent, {
                  class: "tab-window",
                  readonly: readonly.value,
                  onDelete: deleteItem
                }, null, 8, ["readonly"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_tabs_window_item, {
              value: "service-levels-kpis",
              key: "service-levels-kpis"
            }, {
              default: _withCtx(() => [
                _createVNode(ServiceLevelsKPIsComponent, {
                  class: "tab-window",
                  readonly: readonly.value,
                  onDelete: deleteItem
                }, null, 8, ["readonly"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_tabs_window_item, {
              value: "life-cycle-cost-modelling",
              key: "life-cycle-cost-modelling"
            }, {
              default: _withCtx(() => [
                _createVNode(LifeCycleCostModellingComponent, {
                  class: "tab-window",
                  readonly: readonly.value,
                  onDelete: deleteItem
                }, null, 8, ["readonly"])
              ]),
              _: 1
            }),
            _createVNode(_component_v_tabs_window_item, {
              value: "improvement-actions",
              key: "improvement-actions"
            }, {
              default: _withCtx(() => [
                _createVNode(ImprovementActionsComponent, {
                  class: "tab-window",
                  readonly: readonly.value,
                  onDelete: deleteItem
                }, null, 8, ["readonly"])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["model-value"]),
        _createVNode(BaseDialog, {
          modelValue: createRevisionDialog.value,
          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((createRevisionDialog).value = $event)),
          "max-width": "400",
          title: "Create Revision"
        }, {
          actions: _withCtx(() => [
            _createVNode(_component_v_btn, {
              disabled: !newRevisionName.value,
              color: "primary",
              variant: "text",
              loading: creatingRevision.value,
              onClick: createRevision
            }, {
              default: _withCtx(() => [
                _createTextVNode(" Create ")
              ]),
              _: 1
            }, 8, ["disabled", "loading"])
          ]),
          default: _withCtx(() => [
            _createVNode(_component_v_text_field, {
              modelValue: newRevisionName.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newRevisionName).value = $event)),
              label: "Revision Name",
              density: "compact",
              variant: "outlined",
              "hide-details": ""
            }, null, 8, ["modelValue"]),
            _hoisted_3
          ]),
          _: 1
        }, 8, ["modelValue"])
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_4, [
        _createVNode(_component_v_progress_circular, {
          size: 70,
          width: 7,
          color: "blue",
          indeterminate: ""
        })
      ]))
}
}

})